// lib/SignSendConfirmVersionedTransaction.tsx

import {
    AnchorError,
    parseIdlErrors,
    ProgramError,
    ProgramErrorStack,
    translateError,
  } from '@coral-xyz/anchor';
  import { Wallet } from '@coral-xyz/anchor/dist/cjs/provider';
  import { Connection, TransactionConfirmationStatus, VersionedTransaction } from '@solana/web3.js';
  import { IDL } from 'hedge-web3';
  import { connect } from 'react-redux';
  import { toast } from 'react-toastify';
  import mixpanel from 'mixpanel-browser';
  
  import { AppDispatch } from '../pages/_app';
  import {
    setLoadingScreenError,
    setLoadingScreenMessage,
    setLoadingScreenShowing,
    setLoadingScreenTitle,
    setLoadingScreenTransactionSteps,
  } from '../reducers/loadingScreenReducer';
  
  import { SystemErrors } from './SystemErrors';
  import TransactionStep, { StepStatus } from './TransactionStep';
  
  /**
   * SignSendConfirmVersionedTransaction is similar to SignSendConfirmTransaction,
   * but it expects each TransactionStep to build a VersionedTransaction rather
   * than a legacy Transaction. The rest of the flow is almost the same.
   */
  export async function SignSendConfirmVersionedTransaction(
    connection: Connection,
    Wallet: Wallet, // Must be a wallet that can sign VersionedTransactions
    steps: TransactionStep[],
    dispatch: AppDispatch,
    actionTitle: string
  ): Promise<string> {
    console.log('SendSendConfirm for Transaction (Versioned)')

    // Show loading screen / set UI states
    dispatch(setLoadingScreenTitle(actionTitle));
    dispatch(setLoadingScreenShowing(true));
    dispatch(setLoadingScreenTransactionSteps(steps));
  
    mixpanel.track('SignSendConfirmVersionedTransaction Start', { actionTitle });
  
    for (let index = 0; index < steps.length; index++) {
      // 1) Build the VersionedTransaction
      steps[index].status = StepStatus.Building;
      dispatch(setLoadingScreenTransactionSteps(steps));
  
      const versionedTx = await steps[index].buildTransaction() as VersionedTransaction;
      console.log(`Transaction for step`, steps[index].title, versionedTx);
  
      if (!versionedTx) {
        // If buildTransaction() returned null/undefined, skip
        steps[index].status = StepStatus.Skip;
        dispatch(setLoadingScreenTransactionSteps(steps));
        continue;
      }
  
      // 2) Sign
      steps[index].status = StepStatus.Signing;
      dispatch(setLoadingScreenTransactionSteps(steps));
  
      let signedTx: VersionedTransaction;
      try {
        // The wallet must implement signTransaction(VersionedTransaction)
        signedTx = await Wallet.signTransaction(versionedTx as VersionedTransaction) 
      } catch (error) {
        steps[index].error = parseErrorMessage(error);
        steps[index].status = StepStatus.Fail;
        dispatch(setLoadingScreenTransactionSteps(steps));
        mixpanel.track('SignSendConfirmVersionedTransaction Transaction Failed', {
          transactionTitle: steps[index].title,
        });
        // If signing fails, break out
        return Promise.reject(error);
      }
  
      // 3) Send
      steps[index].status = StepStatus.Submitting;
      dispatch(setLoadingScreenTransactionSteps(steps));
  
      try {
        const signature = await connection.sendTransaction(signedTx);
        steps[index].signature = signature;
        steps[index].status = StepStatus.Processing;
        dispatch(setLoadingScreenTransactionSteps(steps));
  
        // 4) Confirm 'processed'
        await confirmTransaction(connection, signature, 'processed');
  
        // 5) Confirm 'confirmed'
        steps[index].status = StepStatus.Confirming;
        dispatch(setLoadingScreenTransactionSteps(steps));
  
        await confirmTransaction(connection, signature);
  
        steps[index].status = StepStatus.Success;
        dispatch(setLoadingScreenTransactionSteps(steps));
        mixpanel.track('SignSendConfirmVersionedTransaction Transaction Success', {
          transactionTitle: steps[index].title,
          signature,
        });
      } catch (error) {
        steps[index].error = parseErrorMessage(error);
        steps[index].status = StepStatus.Fail;
        dispatch(setLoadingScreenTransactionSteps(steps));
        mixpanel.track('SignSendConfirmVersionedTransaction Transaction Failed', {
          transactionTitle: steps[index].title,
        });
        // End loop on error
        break;
      }
    }
  
    mixpanel.track('SignSendConfirmVersionedTransaction Finish', { actionTitle });
    return 'Done';
  }
  
  export async function confirmTransaction(
    connection: Connection,
    signature: string,
    desiredStatus: TransactionConfirmationStatus = 'confirmed',
    timeout: number = 60000
  ): Promise<void> {
    const start = Date.now();
    
    while (Date.now() - start < timeout) {
      const response = await connection.getSignatureStatuses([signature]);
      const status = response.value[0];
      
      if (!status) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }

      if (status.err) {
        throw status.err;
      }

      if (!status.confirmationStatus) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }

      if (status.confirmationStatus === desiredStatus || status.confirmationStatus === 'finalized') {
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    throw new Error(`Transaction confirmation timeout after ${timeout}ms`);
  }
  
  /**
   * parseErrorMessage replicates your existing logic to parse known Anchor / IDL-coded errors
   * or fallback to the raw error message.
   */
  function parseErrorMessage(error: any): string {
    console.log('parseErrorMessage', error);
    console.log('parseErrorMessage', JSON.stringify(error));
  
    if (!error) {
      return 'No error to parse';
    }
    if (error?.code) {
      console.log('Parsed Error Code:', error.code);
    }
    const parsedError = translateError(error, parseIdlErrors(IDL));
    console.log('Transaction Error:', parsedError);
    console.log('Transaction Error:', JSON.stringify(parsedError));
  
    if (parsedError?.error?.errorMessage) {
      console.log('parsedError?.error?.errorMessage', parsedError?.error?.errorMessage);
      return parsedError?.error?.errorMessage;
    }
  
    const parsedErrorMessage = parseErrorText(parsedError.toString());
    console.log('parsedError.toString()', parsedError.toString());
    if (parsedErrorMessage) {
      console.log('parsedErrorMessage', parsedErrorMessage);
      return parsedErrorMessage;
    }
  
    if (error?.message) {
      console.log('error?.message:', error?.message);
      return error.message;
    }
  
    return 'Unknown error';
  }
  
  function parseErrorText(text: string) {
    const search = /error: (0x[0-9a-fA-F]+)/gm;
    const match = search.exec(text);
    console.log('match', match);
    if (match && match.length >= 2) {
      console.log('group', match[1]);
      const baseTen = parseInt(match[1], 16);
      console.log('baseTen', baseTen);
      if (getErrorMessageForCode(baseTen)) {
        return getErrorMessageForCode(baseTen);
      }
    }
    return '';
  }
  
  function getErrorMessageForCode(code: number) {
    console.log('Checking for error number:', code);
    if (SystemErrors[code]) {
      return SystemErrors[code];
    }
    return '';
  }
  